import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import { DatePicker } from "@mui/x-date-pickers";
import {
  useAddLegalCapitalMutation,
  useDeleteLegalCapitalPaymentMutation,
  useGetCapitalList,
  useGetLegalCapitalById,
  useUpdateLegalCapitalMutation,
} from "src/api/settings/capital";
import { useGetPaymentMethods } from "src/api/generic";
import moment from "moment";
import { convertObjectToFormData } from "src/utils";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { ConfirmPopup } from "src/shared/components/confirm-popup";

interface IModel {
  open: boolean;
  handleClose: () => void;
  row?: any;
  refetch?: Function;
}

const schema = yup.object().shape({
  amount: yup.number().required("Required"),
  date: yup.date().required("Required"),
  owners: yup
    .array()
    .of(
      yup.object().shape({
        capital_id: yup.string().required("required"),
        item_id: yup.string(),
        name: yup.string().required("required"),
        amount: yup.string().required("required"),
        payment_method_id: yup.string(),
        payment_amount: yup
          .string()

          .test("min-value", "Must be greater than 0", (value) => {
            if (!value) return true;
            return parseFloat(value) > 0;
          }),
      }),
    )
    .required("Required"),
  note: yup.string(),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const AddEditLegalCapital = (props: IModel) => {
  const { open, handleClose, row, refetch = () => {} } = props;
  const { t } = useTranslation("settings");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const mode = !row ? "add" : "edit";
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [files, setFiles] = useState<any>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [clickedPayment, setClickedPayment] = useState<any>();

  // APIs
  const { data, isFetching, refetch: refetchLegalCapital } = useGetLegalCapitalById({
    id: `${row?.id}`,
  });

  const capitalToEdit = data?.data;

  const { data: capitalData } = useGetCapitalList();
  const { mutate, status, error, isPending } = useAddLegalCapitalMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateLegalCapitalMutation();
  const { data: paymentsData } = useGetPaymentMethods();
  const {
    mutate: mutateDelete,
    status: statusDelete,
    error: errorDelete,
    isPending: isPendingDelete,
  } = useDeleteLegalCapitalPaymentMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: { owners: [] },
  });

  const { fields, append, remove, update } = useFieldArray({
    name: "owners",
    control,
  });

  const removeItem = (index: number) => {
    remove(index);
  };

  // Handler function for when an owner is selected
  const handleItemSelect = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedItem(newValue);
    if (newValue) {
      append({
        capital_id: newValue.id,
        amount: "",
        name: newValue.name,
      });
      setInputValue("");
      setSelectedItem(null);
    }
  };

  // Handle manual changes to the input field
  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    if (selectedItem === null) {
      setInputValue(newInputValue);
    }
  };

  const handleCloseDelete = () => setOpenDelete(false);

  const toDeletePayment = (payment: any) => {
    setOpenDelete(true);
    setClickedPayment(payment);
  };

  const handleDeletePayment = (id: number) => {
    mutateDelete(clickedPayment?.id);
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { owners, date, amount, note } = data;
    if (owners.length === 0) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("selectAtLeastOneOwner"),
        },
      ]);
      return;
    }

    const accumulatedOwnersAmount = owners.reduce((total, owner) => {
      const value = Number(owner.amount);
      return total + (isNaN(value) ? 0 : value);
    }, 0);

    if (accumulatedOwnersAmount !== +amount) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("totalOwnersAmountMustEqualCapitalAmount"),
        },
      ]);
      return;
    }

    const dataToSend = {
      date: moment(date).format("YYYY-MM-DD"),
      amount,
      ...(note && { note: note }),
    };

    if (mode === "add") {
      const formData = convertObjectToFormData(dataToSend);
      // append owners
      if (owners.length > 0) {
        owners.forEach((owner, index: number) => {
          formData.append(`owners[${index}][capital_id]`, owner.capital_id);
          formData.append(`owners[${index}][amount]`, owner.amount);
          if (owner.payment_amount && owner.payment_method_id) {
            formData.append(
              `owners[${index}][payments][0][amount]`,
              owner.payment_amount ?? "",
            );
            formData.append(
              `owners[${index}][payments][0][payment_method_id]`,
              owner.payment_method_id ?? "",
            );
          }
        });
      }
      // append files
      if (files.length > 0) {
        files.forEach((file: any, index: number) => {
          formData.append(`files[${index}]`, file);
        });
      }
      mutate(formData);
    } else {
      mutateUpdate({
        id: row.id,
        ...dataToSend,
        owners: owners.map((item) => {
          return {
            capital_id: item.capital_id,
            amount: item.amount,
          };
        }),
      });
    }
  };

  useEffect(() => {
    if (!!capitalToEdit) {
      reset({
        amount: capitalToEdit.amount ?? "",
        date: capitalToEdit.date
          ? moment(capitalToEdit.date, "YYYY-MM-DD").toDate()
          : undefined,
        note: capitalToEdit.note ?? "",
        owners: capitalToEdit.owners.map((item: any) => {
          return {
            capital_id: item.capital_id,
            item_id: item.id,
            name: item.capital?.name,
            amount: item.amount,
          };
        }),
      });
    }
  }, [capitalToEdit]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  //Delete payment success and error handling
  useEffect(() => {
    if (statusDelete === "success") {
      handleCloseDelete();
      refetchLegalCapital();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("deletedSuccessfully"),
        },
      ]);
    } else if (statusDelete === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorDelete?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusDelete]);

  const selectedOwnersIds = watch("owners")?.map((item) => +item.capital_id);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          spacing={3}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: 557 },
            bgcolor: "#FFF",
            border: "1px solid #D0D5DD",
            borderRadius: 1,
            boxShadow: 24,
            p: 3,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          {isFetching && mode !== "add" ? (
            <Box
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              minHeight={"400px"}
              pb={"50px"}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                p={0}
              >
                <Typography color={"#101828"} fontSize={"20px"}>
                  {mode === "add"
                    ? t("addLegalCapital")
                    : t("editLegalCapital")}
                </Typography>
                <IconButton onClick={handleClose} sx={{ p: 0 }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    label={t("capitalAmount")}
                    fullWidth
                    size="small"
                    {...field}
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                  />
                )}
              />
              {/* date */}
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.date,
                        helperText: t(`${errors.date?.message ?? ""}`),
                        size: "small",
                        inputProps: {
                          readOnly: true,
                          onKeyDownCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                          onPasteCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                        },
                      },
                    }}
                    format={"dd/MM/yyyy"}
                    label={generalT("date")}
                  />
                )}
              />

              {fields.map((itemField, index) => {
                const itemDetails = capitalToEdit?.owners?.find(
                  (item: any) => item.id === itemField.item_id,
                );
                return (
                  <Stack
                    key={itemField.id}
                    // direction={{ xs: "column", md: "row" }}
                    padding={"8px 16px"}
                    borderRadius={1.5}
                    border={"1px solid #F0F0F0"}
                    spacing={"21px"}
                  >
                    <Stack
                      direction={"row"}
                      justifyContent={"flex-end"}
                      mt={0.5}
                    >
                      <IconButton
                        // variant="outlined"
                        size="small"
                        sx={{ alignSelf: "flex-start" }}
                        onClick={() => removeItem(index)}
                        color="error"
                      >
                        <img src={DeleteIcon} alt="" />
                      </IconButton>
                    </Stack>

                    <Box display={"flex"} gap={"11px"}>
                      <Box width={"50%"}>
                        <Typography
                          color={"#667085"}
                          fontSize={"14px"}
                          lineHeight={"24px"}
                        >
                          {t("ownerName")}
                        </Typography>
                        <Typography
                          color={"#667085"}
                          fontSize={"14px"}
                          lineHeight={"24px"}
                          fontWeight={700}
                        >
                          {itemField.name}
                        </Typography>
                      </Box>
                      <Controller
                        name={`owners.${index}.amount`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={t("capitalAmount")}
                            variant="outlined"
                            {...field}
                            error={!!errors.owners?.[index]?.amount}
                            helperText={errors.owners?.[index]?.amount?.message}
                            fullWidth
                            sx={{ maxWidth: "50%" }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                    {mode === "add" ? (
                      <Stack
                        direction={"row"}
                        gap={"11px"}
                        alignItems={"center"}
                      >
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.owners?.[index]?.payment_method_id}
                        >
                          <InputLabel id="demo-simple-select-label">
                            {t("paymentMethodOptional")}
                          </InputLabel>
                          <Controller
                            name={`owners.${index}.payment_method_id`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                key={field.value}
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label={t("paymentMethodOptional")}
                              >
                                {paymentsData?.data
                                  ?.filter((pay) => pay.status)
                                  .map(({ id, name }) => {
                                    return (
                                      <MenuItem key={id} value={`${id}`}>
                                        {name}
                                      </MenuItem>
                                    );
                                  })}
                              </Select>
                            )}
                          />
                          <FormHelperText id="my-helper-text">
                            {errors.owners?.[index]?.payment_method_id?.message}
                          </FormHelperText>
                        </FormControl>

                        <Controller
                          name={`owners.${index}.payment_amount`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              type="number"
                              label={generalT("amount")}
                              variant="outlined"
                              {...field}
                              error={!!errors.owners?.[index]?.payment_amount}
                              helperText={
                                errors.owners?.[index]?.payment_amount?.message
                              }
                              fullWidth
                              size="small"
                            />
                          )}
                        />
                      </Stack>
                    ) : (
                      <Stack gap={"11px"}>
                        {itemDetails?.payments?.map((pay: any) => {
                          return (
                            <Stack
                              direction={"row"}
                              gap={"11px"}
                              alignItems={"center"}
                            >
                              <TextField
                                label={t("paymentMethodOptional")}
                                value={pay.payment_method?.name}
                                fullWidth
                                size="small"
                                disabled
                              />
                              <TextField
                                label={generalT("amount")}
                                value={pay.amount}
                                fullWidth
                                size="small"
                                disabled
                              />
                              <IconButton
                                size="small"
                                onClick={() => toDeletePayment(pay)}
                                color="error"
                                disabled={isPendingDelete}
                              >
                                <img src={DeleteIcon} alt="" />
                              </IconButton>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Stack>
                );
              })}

              {true ? (
                <>
                  <Autocomplete
                    size="small"
                    disablePortal
                    id="combo-box-demo"
                    options={
                      capitalData?.data.filter(
                        (item: any) => !selectedOwnersIds?.includes(item.id),
                      ) || []
                    }
                    sx={{ width: 235 }}
                    getOptionLabel={(option: any) => option.name}
                    value={selectedItem}
                    onChange={handleItemSelect}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    renderInput={(params: any) => (
                      <TextField {...params} label={t("addAnotherOwner")} />
                    )}
                  />
                </>
              ) : null}

              {/* summary */}
              <Stack p={2} gap={1.5} bgcolor={"#F9FAFB"}>
                <Typography fontSize={"16px"} color={"#344054"}>
                  {generalT("summary")}
                </Typography>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {t("capital")}:
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {watch("amount")} {generalT("sar")}
                  </Typography>
                </Stack>
                {watch("owners").map((owner) => (
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Typography fontSize={"14px"} color={"#344054"}>
                      {owner.name}:
                    </Typography>
                    <Typography
                      fontSize={"14px"}
                      fontWeight={600}
                      color={"primary"}
                    >
                      {(+owner.amount / +watch("amount")) * 100}%
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              {/* note */}
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={generalT("notesOptional")}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    maxRows={3}
                    {...field}
                    error={!!errors.note}
                    helperText={errors.note?.message}
                  />
                )}
              />

              {/* files */}
              {mode === "add" && (
                <Box width={"100%"} mt={2}>
                  <FilePondUploader
                    onUpload={(e) => setFiles(e)}
                    maxFiles={3}
                    maxFileSize={3}
                    acceptedFileTypes={[
                      "image/*",
                      "application/pdf",
                      "text/csv",
                      "application/vnd.ms-excel",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    ]}
                  />
                </Box>
              )}

              <Stack spacing={2} direction={"row"} mt={3}>
                <Button
                  variant="outlined"
                  color="tertiary"
                  fullWidth
                  onClick={handleClose}
                >
                  {generalT("cancel")}
                </Button>
                <LoadingButton
                  variant="contained"
                  fullWidth
                  type="submit"
                  loading={isPending || isPendingUpdate}
                >
                  {mode === "add" ? generalT("submit") : generalT("save")}
                </LoadingButton>
              </Stack>
            </>
          )}
          {/* confirm delete payment popup */}
          <ConfirmPopup
            open={openDelete}
            item={clickedPayment?.payment_method?.name}
            handleClose={handleCloseDelete}
            handleConfirm={handleDeletePayment}
            isPending={isPendingDelete}
          />
        </Stack>
      </form>
    </Modal>
  );
};

export default AddEditLegalCapital;
